<template>
  <div class="top" :class="whiteHeader ? 'white-header' : 'green-header'">
    <div class="actions">
      <ChatButton
        v-if="showBack"
        class="arrow-back"
        @click="onBack"
      >
        <Icon art="regular" name="arrow-left-long" />
      </ChatButton>
      <div class="buttons-wrapper right">
        <ChatButton v-if="!hideMinimize"
          class="button hover-style minimize"
          :types="['round']"
          @click="onMinimize"
        >
          <Icon
            art="regular"
            name="window-minimize"
            :color="iconColor"
            :width="14"
          />
        </ChatButton>
        <ChatButton
          v-if="!mobile"
          class="button hover-style full-screen"
          :types="['round', 'endflow']"
          @click="toggleFullScreenInner"
        >
          <Icon
            v-if="!fullScreen"
            art="regular"
            name="expand"
            :color="iconColor"
          />
          <Icon
            v-if="fullScreen"
            art="regular"
            name="compress"
            :color="iconColor"
          />
        </ChatButton>
        <ChatButton
          v-if="showFinish && !disableFinish"
          class="button clear"
          :types="['round', 'endflow']"
          :class="{'hover-style': !disableFinish}"
          :disabled="disableFinish"
          @click="onEnd"
        >
          <Icon
            art="regular"
            name="xmark"
            :color="iconColor"
            :width="22"
            :height="22"
          />
        </ChatButton>
      </div>
    </div>
    <div v-if="!isCustomerFlowEnd && !whiteHeader && !conversationEnd && !isArchiveView" class="info">
      <img
        :src="getSenderIcon(getActiveAgent, chatbotActive)"
        :alt="defaultAgentScreenName"
        :class="['message-picto', {
          'message-picto-default': !chatbotActive &&
            getActiveAgent.type && getActiveAgent.type === 'default',
          'aiaibot-agent': isAiaiBotAgent
        }]"
      />
      <div class="agent">
        <p class="name">
          {{ getActiveAgent.agentName || getActiveAgent.screenName }}
        </p>
        <small class="status" v-if="getAgentStatus">
          {{ t(getAgentStatus) }}
        </small>
      </div>
    </div>
  </div>
</template>

<script>
import ChatButton from './ChatButton'
import Icon from './Icon'
import { useI36n } from '@jota-one/i36n/src/index.vue2'
import { mapState, mapActions, mapGetters } from 'vuex'
import Vue from 'vue'
import { EVENT, dispatchCustomEvent } from '@/helpers/customEvents'

export default {
  components: {
    ChatButton,
    Icon
  },
  name: 'TopHeader',
  props: {
    showBack: Boolean,
    showFinish: Boolean,
    disableFinish: Boolean,
    hideMinimize: Boolean,
    mobile: Boolean
  },

  setup () {
    const { $label: t } = useI36n()

    return { t }
  },

  computed: {
    ...mapGetters('conversation', ['getActiveAgent', 'getAgentStatus', 'isAiaiBotAgent']),
    ...mapState('conversation', ['conversation', 'chatbotActive']),
    ...mapGetters('uistate', ['isArchiveView', 'getBehavior', 'defaultIncomingIcon', 'chatbotIncomingIcon', 'defaultAgentScreenName', 'aiaiBotIncomingIcon', 'isCustomerFlow']),
    ...mapState('uistate', ['fullScreen', 'conversationState']),

    isCustomerFlowEnd () {
      if (!process.env.VUE_APP_TNPS_ACTIVE) {
        return false
      }

      return (
        this.conversationState === 'CONVERSATION_RATING' &&
        this.isCustomerFlow
      )
    },

    whiteHeader () {
      return this.conversationState === 'CONVERSATION_ENDED' ||
        this.conversationState === 'CONVERSATION_TRANSCRIPT' ||
        this.conversationState === 'CONVERSATION_SUSPENDED' ||
        this.conversationState === 'CONVERSATION_RATING'
    },
    conversationEnd () {
      return this.conversationState === 'CONVERSATION_RATED' || this.conversationState === 'CONVERSATION_NEW'
    },
    iconColor () {
      return this.whiteHeader ? 'black' : 'white'
    }
  },
  methods: {
    ...mapActions('uistate', ['toggleFullScreen', 'setArchiveView', 'setOpen']),
    onEnd () {
      this.$emit('clickFinish')
    },
    onMinimize () {
      this.$emit('clickMinimize')
      if (this.getBehavior === 'passive') {
        dispatchCustomEvent(EVENT.SEND.MINIMIZE)
      }
    },
    onBack () {
      this.setArchiveView(false)
      setTimeout(() => {
        this.$emit('clickScrollToEnd')
      }, 50)
    },
    toggleFullScreenInner () {
      this.toggleFullScreen()
      let that = this
      setTimeout(() => {
        that.$emit('clickScrollToEnd')
      }, 50)
    },
    getSenderIcon (agent, isChatBotActive) {
      if (isChatBotActive) {
        return this.chatbotIncomingIcon
      }

      if (this.isAiaiBotAgent) {
        return this.aiaiBotIncomingIcon
      }

      if (!agent) {
        return this.defaultIncomingIcon
      }

      let profileId

      if (agent.userId > 0) {
        profileId = agent.userId
      }

      if (agent.senderId > 0) {
        profileId = agent.senderId
      }

      if (!profileId) {
        return this.defaultIncomingIcon
      }

      const baseUrl = Vue.prototype.$chatApi.settings.baseUrl

      return `${baseUrl}/profile/${profileId}/profilePicture`
    }
  }
}
</script>

<style scoped lang="postcss">
/deep/ svg {
  width: 24px;
  height: 24px;
}

.black {
  fill: #000;
}

.white {
  fill: #fff
}

.white-header {
  background-color: #fff;
}

.green-header {
  background-color: #004435;
}

.top {
  padding: 15px;

  .info {
    margin-top: 5px;
    display: flex;

    .agent {
      display: inline-grid;
      align-items: center;
      padding: 0;

      .name {
        font-weight: bold;
        color: white;
      }

      .status {
        color: white;
        text-transform: lowercase;
      }
    }

    .message-picto {
      width: 40px;
      height: 40px;
      border-radius: 50%;
      margin-right: 15px;
      background-color: white;
      border: 1px solid gainsboro;
      padding: 3px;

      &.aiaibot-agent {
        width: 50px;
        height: 50px;
        margin-top: -10px;
        margin-right: 10px;
        background: none;
        border: none;
      }
    }

    .message-picto-default {
      border: 1px solid white;
    }
  }

  .actions {
    display: flex;
    justify-content: space-between;

    .right {
      margin-left: auto;
    }

    .buttons-wrapper {
      display: flex;
      align-items: center;
    }

    .button {
      margin: 0;
      border: none;
      z-index: 2;
      line-height: 1px;
      height: auto;
      min-height: auto;

      &.minimize {
        border-radius: 50%;
        height: 24px;
        width: 24px;
        min-width: 24px;
        margin: 0;
        border: none;
      }

      &.full-screen {
        margin-left: 15px;
        border-radius: 50%;
        height: 24px;
        width: 24px;
        min-width: 24px;
        display: flex;
        justify-content: center;
        align-items: center;
      }

      &.clear {
        margin-left: 15px;
        height: 24px;
        width: 24px;
        min-width: 24px;
      }

      &.arrow-back {
        height: 24px;
        width: 24px;
        min-width: 24px;
        padding: 0;
        background: transparent;
      }
    }
  }
}
</style>
